// 레이(Lay) 챗봇 호출. 서버 계약은 docs/ops/docs-chatbot-plan.md §5-1.
//
// 서버는 "사용자가 사이트에서 볼 수 있는 문서" 만 근거로 답한다. 역할 판정은 서버가
// Authorization 토큰으로 하므로 클라이언트는 역할이나 문서 본문을 보내지 않는다 —
// 보내는 것은 질문·대화 id·지금 보고 있는 slug 뿐이다.

export const MAX_MESSAGE = 500;

export function scopeFromAudiences(audiences) {
  const has = (a) => audiences && audiences.has(a);
  if (has('internal')) return 'internal'; // 내부 직원(2026-10-15) — 서버 scope 와 같은 이름. 대화 저장소 키로만 쓴다
  if (has('creator') && has('rights')) return 'all';
  if (has('rights')) return 'rights';
  if (has('creator')) return 'creator';
  return 'public';
}

const GENERIC_ERROR = '지금 답변 생성이 어려워요. 잠시 후 다시 시도해 주세요.';

export class LayError extends Error {
  constructor(status, content, retryAfter) {
    super(content);
    this.name = 'LayError';
    this.status = status;
    this.content = content;
    this.retryAfter = retryAfter;
  }
}

async function post({ url, accessToken, payload }) {
  if (!url) throw new LayError(0, '레이 서버 주소가 설정되지 않았어요.');
  const headers = { 'Content-Type': 'application/json' };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let res;
  try {
    res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
  } catch (e) {
    throw new LayError(0, GENERIC_ERROR);
  }

  let data = {};
  try { data = await res.json(); } catch (e) { data = {}; }

  if (res.status === 429) {
    throw new LayError(429, data.content || '질문이 잠시 많이 몰렸어요. 조금 뒤에 다시 시도해 주세요.', 60);
  }
  if (res.status === 403) {
    throw new LayError(403, data.error === 'not_conversation_owner' ? '이 대화는 지금 세션의 것이 아니에요. 새 대화로 다시 물어봐 주세요.' : '이 주소에서는 레이를 쓸 수 없어요.');
  }
  if (!res.ok) throw new LayError(res.status, data.content || GENERIC_ERROR);
  return data;
}

// 서버 응답의 escalation 을 정규화한다. v11 이전 서버(escalation 없음)는 escalated 면 이미 넣은 것('sent').
export function normalizeEscalation(data) {
  if (data && (data.escalation === 'offer' || data.escalation === 'sent')) return data.escalation;
  return data && data.escalated ? 'sent' : 'none';
}

// 위젯에 저장된 레이 답의 에스컬레이션 상태: 'none' | 'offer'(되묻는 중) | 'sent' | 'declined'. v11 이전 저장본(escalation 없음)은 이미 넘어간 것.
export function escalationState(m) {
  if (!m || m.role === 'user' || !m.escalated) return 'none';
  return m.escalation === 'offer' || m.escalation === 'sent' || m.escalation === 'declined' ? m.escalation : 'sent';
}

// 성공: { content, sources[], conversationId, messageId, escalated, escalation: 'none'|'offer'|'sent', scope }
// 실패: throw LayError { status, content, retryAfter? }
// v11(2026-09-10): escalate_mode:'ask' — 가이드에 없는 답이어도 서버가 바로 에스컬레이션하지 않고 'offer' 로 돌려준다.
// 사용자가 「예」를 누르면 escalateLay() 가 넣는다(기획 lay-ai-operations-plan.md §3-5 (나)).
export async function askLay({ url, message, conversationId, currentSlug, accessToken, honeypot }) {
  const data = await post({
    url, accessToken,
    payload: {
      message: message.slice(0, MAX_MESSAGE),
      ...(conversationId ? { conversation_id: conversationId } : {}),
      ...(currentSlug ? { current_slug: currentSlug } : {}),
      escalate_mode: 'ask',
      _hp: honeypot || '',
    },
  });
  return {
    content: String(data.content || ''),
    sources: Array.isArray(data.sources) ? data.sources : [],
    conversationId: data.conversation_id || conversationId || null,
    messageId: data.message_id || null,
    escalated: Boolean(data.escalated),
    escalation: normalizeEscalation(data),
    scope: data.scope || null,
  };
}

// 「운영팀에 물어볼까요?」 → 예. 그 답(messageId)의 질문을 lay_escalations 에 넣는다. 성공: { escalationId } · 실패: throw LayError
export async function escalateLay({ url, conversationId, messageId, accessToken }) {
  if (!conversationId) throw new LayError(400, '이 대화는 아직 저장되지 않아 전달할 수 없어요. 다시 한 번 물어봐 주세요.');
  const data = await post({ url, accessToken, payload: { action: 'escalate', conversation_id: conversationId, ...(messageId ? { message_id: messageId } : {}) } });
  if (!data.ok) throw new LayError(500, '전달에 실패했어요. 다시 시도해 주세요.');
  return { escalationId: data.escalation_id || null, duplicate: Boolean(data.duplicate) };
}

// ───────────────────────────────────────────────────────────────────────────
// 미리보기 응답기 — 프리뷰 빌드는 외부 fetch 가 막혀 있어 실제 레이를 부를 수 없다.
// 시안 검토가 목적이므로 window.__DOCS__ 에 실린 문서에서 질문 단어가 가장 많이 겹치는
// 문단을 그대로 돌려준다. AI 답변이 아니며 화면에 "미리보기 응답" 으로 표시한다.
// 근거가 없으면 실제 서버와 같은 형태로 escalated 를 돌려주므로 빈 상태·에스컬레이션
// 화면까지 시안에서 확인할 수 있다.

const STOP = new Set([
  '어떻게', '어떤', '뭔가요', '뭐예요', '무엇', '언제', '어디', '어디서', '왜', '하나요', '있나요',
  '되나요', '인가요', '할까요', '해요', '해주세요', '알려주세요', '알려줘', '좀', '제', '내', '저',
  '그', '이', '것', '수', '때', '및', '또는', '그리고', '있어요', '없어요', '건가요',
]);
const PARTICLES = /(은|는|이|가|을|를|에서|으로|로|의|도|과|와|에|께|까지|부터|처럼|이나|나|요)$/;

function tokenize(q) {
  return q
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .map((w) => (w.length > 2 ? w.replace(PARTICLES, '') : w))
    .filter((w) => w.length >= 2 && !STOP.has(w));
}

function sections(md) {
  const out = [];
  let heading = null;
  let buf = [];
  const flush = () => {
    const body = buf.join('\n').trim();
    if (body) out.push({ heading, body });
    buf = [];
  };
  md.split('\n').forEach((line) => {
    if (/^#\s/.test(line)) return; // 문서 제목
    if (/^최종 수정:/.test(line.trim())) return;
    if (/^##\s/.test(line)) { flush(); heading = line.replace(/^##\s+/, '').trim(); return; }
    buf.push(line);
  });
  flush();
  return out;
}

function trimAnswer(text, max = 360) {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const at = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('다.'), cut.lastIndexOf('\n'));
  return `${cut.slice(0, at > 120 ? at + 1 : max).trim()} …`;
}

export function demoAnswer(message, pages, docs, currentSlug) {
  const terms = tokenize(message);
  const scored = [];
  pages.forEach((page) => {
    const md = docs[page.slug];
    if (!md) return;
    sections(md).forEach((sec) => {
      sec.body.split(/\n\s*\n/).forEach((para) => {
        const p = para.trim();
        if (!p || /^\|/.test(p) || p.length < 20) return;
        const hay = `${sec.heading || ''}\n${p}`.toLowerCase();
        let score = terms.reduce((n, t) => n + (hay.includes(t) ? 1 : 0), 0);
        if (score && sec.heading && terms.some((t) => sec.heading.toLowerCase().includes(t))) score += 1;
        if (score && page.slug === currentSlug) score += 0.5;
        if (score > 0) scored.push({ score, page, heading: sec.heading, para: p });
      });
    });
  });
  // 단어 하나만 우연히 겹치는 문단("주식" → "주식회사")은 근거로 치지 않는다.
  const minScore = terms.length >= 2 ? 2 : 1;
  const kept = scored.filter((s) => s.score >= minScore).sort((a, b) => b.score - a.score);

  if (!kept.length) {
    return {
      content: '이 가이드에는 그 내용이 없어요. 가이드 문서에 있는 절차나 정책이라면 다른 표현으로 다시 물어봐 주세요.',
      sources: [],
      escalated: true,
      escalation: 'offer', // 실제 서버(v11)와 같이 「운영팀에 물어볼까요?」 되묻기 화면까지 시안에서 본다
      demo: true,
    };
  }

  const best = kept[0];
  const seen = new Set();
  const sources = [];
  kept.forEach((s) => {
    if (sources.length >= 2 || seen.has(s.page.slug)) return;
    seen.add(s.page.slug);
    sources.push({ slug: s.page.slug, title: s.page.title, heading: s.heading });
  });
  return { content: trimAnswer(best.para.replace(/^>\s?/gm, '')), sources, escalated: false, escalation: 'none', demo: true };
}
